import React from 'react';
import { buildPaymentRoadmap, loanRequirementLabel, loanTriggerLabel, percentLabel, validatePaymentCondition, type PaymentCondition } from '../../office/paymentCondition';

const rupees = (value: number) => `₹${Math.round(value).toLocaleString('en-IN')}`;

/** Right-hand panel of the booking form dialog: the plan worked out in rupees and dates as the form changes. */
export default function BookingPlanPanel(props: {
  condition: PaymentCondition;
  bookingDate: string;
  unitAmount: number;
  legacyTerms?: string | null;
  locked?: boolean;
}) {
  const { condition, bookingDate, unitAmount, legacyTerms, locked } = props;
  const hasPrice = Number.isFinite(unitAmount) && unitAmount > 0;
  const amountOf = (percent: number) => (hasPrice ? rupees((unitAmount * percent) / 100) : percentLabel(percent));

  const head = (
    <div className="bf-price">
      <small>Unit price</small>
      <b className="num">{hasPrice ? rupees(unitAmount) : 'Not set'}</b>
      {!hasPrice ? <span className="bf-hint">Add the unit price to see rupee amounts. Percentages are shown for now.</span> : null}
    </div>
  );

  if (legacyTerms != null) {
    return (
      <>
        {head}
        <div className="bf-soft">
          This booking uses older payment terms: <b className="num">{legacyTerms || 'none entered'}</b>. Convert them to see the plan with dates.
        </div>
      </>
    );
  }

  const error = validatePaymentCondition(condition);
  const roadmap = buildPaymentRoadmap(condition, bookingDate);
  const total = condition.stages.reduce((sum, stage) => sum + (Number(stage.percent) || 0), 0);

  return (
    <>
      {head}
      <div className="bf-split" aria-hidden="true">
        {condition.stages.map((stage, index) => (
          <i key={stage.id} style={{ width: `${Math.max(0, Number(stage.percent) || 0)}%`, ['--mix' as any]: `${Math.max(25, 100 - index * 30)}%` }} />
        ))}
      </div>
      <div className="bf-plan">
        {roadmap.map((item) => {
          const rows = item.instalments || [];
          const even = rows.length > 1 && rows.every((row) => row.percent === rows[0].percent);
          return (
            <div className="bf-plan-row" key={item.id}>
              <span>{rows.length ? `${rows.length} monthly instalment${rows.length === 1 ? '' : 's'}` : item.label || 'Stage'}</span>
              <b className="num">{rows.length && even ? `${amountOf(rows[0].percent)} each` : amountOf(item.percent)}</b>
              <small>{rows.length ? `${rows[0].label} to ${rows[rows.length - 1].label}` : item.due}</small>
              <small className="num">{percentLabel(item.percent)}{rows.length && even ? ` · ${percentLabel(rows[0].percent)} each` : ''}</small>
              {rows.length ? (
                <details className="bf-inst">
                  <summary>See every instalment</summary>
                  <ol>
                    {rows.map((row) => (
                      <li key={`${row.label}-${row.due}`}><span>{row.due}</span><span className="num">{amountOf(row.percent)}</span></li>
                    ))}
                  </ol>
                </details>
              ) : null}
            </div>
          );
        })}
        {condition.loan ? (
          <div className="bf-plan-row loan">
            <span>Loan request</span>
            <b className="num">{loanRequirementLabel(condition.loan).replace('₹ ', '₹')}</b>
            <small>{loanTriggerLabel(condition.loan.trigger)}</small>
            <small />
          </div>
        ) : null}
      </div>
      {error ? (
        <div className="bf-note bad" role="status">{error}</div>
      ) : (
        <div className="bf-ok" role="status">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
          Plan adds up to 100%{hasPrice ? ` · ${rupees((unitAmount * total) / 100)}` : ''}
        </div>
      )}
      <div className="bf-soft">
        {locked ? 'The signed form is uploaded, so this plan is locked.' : 'The booking form PDF uses these details. Once the signed form is uploaded, the plan is locked.'}
      </div>
    </>
  );
}
